import { allProducts } from '@/data/products';
import { Product, DemoConfig } from '@/data/products/types';

// Demo interface matching the product-based demo system
export interface Demo {
  id: string;
  title: string;
  description: string;
  category: string;
  path: string;
  isAvailable: boolean;
  tags: string[];
}

/**
 * Dynamic Demo Registry - Automatically manages demo-product relationships
 * This eliminates the need for manual configuration when adding new demos
 */

export interface DemoProductMapping {
  productId: string;
  product: Product;
  demoConfig: DemoConfig;
  demoUrl: string;
  searchKeywords: string[];
}

/**
 * Get all products that have demo configurations
 */
export const getProductsWithDemos = (): Product[] => {
  return allProducts.filter(product => product.demoConfig);
};

/**
 * Get demo configuration for a specific product
 */
export const getDemoConfigForProduct = (productId: string): DemoConfig | null => {
  const product = allProducts.find(p => p.id === productId);
  return product?.demoConfig || null;
};

/**
 * Generate all demo routes dynamically from products with demos
 */
export const getAllDemoRoutes = (): string[] => {
  const productsWithDemos = getProductsWithDemos();
  return productsWithDemos.map(product => product.demoConfig!.url);
};

/**
 * Generate demo-product mappings for search and discovery
 */
export const getDemoProductMappings = (opts?: { liveOnly?: boolean }): DemoProductMapping[] => {
  const productsWithDemos = getProductsWithDemos().filter(p =>
    opts?.liveOnly ? p.status?.toLowerCase() === 'live' : true
  );
  
  return productsWithDemos.map(product => ({
    productId: product.id,
    product,
    demoConfig: product.demoConfig!,
    demoUrl: product.demoConfig!.url,
    searchKeywords: [
      ...product.category.toLowerCase().split(' '),
      ...product.title.toLowerCase().split(' '),
      ...(product.businessTypes || []).map(bt => bt.replace('#', '')),
      ...product.features.slice(0, 5).join(' ').toLowerCase().split(' ')
    ].filter(keyword => keyword.length > 2)
  }));
};

/**
 * Convert products with demos to Demo objects for the demo hub
 */
export const generateDemosFromProducts = (): Demo[] => {
  const productsWithDemos = getProductsWithDemos();
  
  return productsWithDemos.map(product => ({
    id: product.id,
    title: product.title,
    description: product.description,
    category: product.category,
    path: product.demoConfig!.url,
    isAvailable: true,
    tags: [
      ...product.category.toLowerCase().split(' '),
      ...(product.businessTypes || []).map(bt => bt.replace('#', '')),
      'demo', 'interactive'
    ].filter(tag => tag.length > 2)
  }));
};

/**
 * Live-only demos derived from products (status === 'Live')
 */
export const generateLiveDemosFromProducts = (): Demo[] => {
  const liveProductsWithDemos = getProductsWithDemos().filter(p => p.status?.toLowerCase() === 'live');

  return liveProductsWithDemos.map(product => ({
    id: product.id,
    title: product.title,
    description: product.description,
    category: product.category,
    path: product.demoConfig!.url,
    isAvailable: true,
    tags: [
      ...product.category.toLowerCase().split(' '),
      ...(product.businessTypes || []).map(bt => bt.replace('#', '')),
      'demo', 'interactive', 'live'
    ].filter(tag => tag.length > 2)
  }));
};

/**
 * Find best matching demo for search term (any demo)
 */
export const findDemoBySearchTerm = (searchTerm: string): DemoProductMapping | null => {
  const mappings = getDemoProductMappings();
  const lowerSearchTerm = searchTerm.toLowerCase();
  
  // First try exact matches
  const exactMatch = mappings.find(mapping => 
    mapping.searchKeywords.some(keyword => keyword === lowerSearchTerm)
  );
  
  if (exactMatch) return exactMatch;
  
  // Then try partial matches
  const partialMatch = mappings.find(mapping =>
    mapping.searchKeywords.some(keyword => keyword.includes(lowerSearchTerm))
  );
  
  return partialMatch || null;
};

/**
 * Find best matching demo for search term (live-only)
 */
export const findLiveDemoBySearchTerm = (searchTerm: string): DemoProductMapping | null => {
  const mappings = getDemoProductMappings({ liveOnly: true });
  const q = (searchTerm || '').toLowerCase().trim();
  if (!q) return null;

  // Tokenize and expand with simple synonyms
  const baseTokens = Array.from(new Set(q.split(/[^a-z0-9]+/g).filter(t => t.length > 1)));
  const synonymMap: Record<string, string[]> = {
    facilities: ['facility', 'fm', 'building', 'workplace', 'workspace', 'property'],
    facility: ['facilities', 'fm', 'building', 'workplace', 'workspace', 'property'],
    coworking: ['workspace', 'workplace', 'shared', 'flex', 'flexible', 'shared-office'],
    workspace: ['coworking', 'workplace'],
    fm: ['facilities', 'facility']
  };
  const expandedTokens = new Set<string>(baseTokens);
  baseTokens.forEach(t => (synonymMap[t] || []).forEach(s => expandedTokens.add(s)));

  // Scoring function
  const scoreMapping = (m: DemoProductMapping): number => {
    const category = m.product.category.toLowerCase();
    const title = m.product.title.toLowerCase();
    const business = (m.product.businessTypes || []).map(bt => bt.replace('#', '').toLowerCase());
    const keywords = m.searchKeywords;

    let score = 0;

    expandedTokens.forEach(tok => {
      if (!tok) return;
      if (category.includes(tok)) score += 5;
      if (title.includes(tok)) score += 4;
      if (business.some(b => b.includes(tok))) score += 3;
      if (keywords.some(k => k === tok)) score += 2;
      else if (keywords.some(k => k.includes(tok))) score += 1;
    });

    // Intent nudges
    const wantsFacilities = ['facilities', 'facility', 'fm'].some(t => expandedTokens.has(t));
    const wantsCoworking = ['coworking', 'workspace', 'workplace'].some(t => expandedTokens.has(t));

    if (wantsFacilities) {
      if (category.includes('facilities')) score += 3;
      if (title.includes('facilities')) score += 2;
      if (m.product.id === 'facilities-management-suite') score += 2;
      if (/(marketing|sales|payroll|hr)/.test(category) || /(marketing|sales)/.test(title) || /marketing-sales/.test(m.product.id)) {
        score -= 6; // avoid marketing/sales when user asks for facilities
      }
    }

    if (wantsCoworking) {
      if (title.includes('coworking')) score += 3;
      if (business.some(b => b.includes('cowork'))) score += 2;
    }

    return score;
  };

  // Compute scores and get best match
  const ranked = mappings
    .map(m => ({ m, s: scoreMapping(m) }))
    .sort((a, b) => b.s - a.s);

  const top = ranked[0];
  if (!top || top.s <= 0) return null;
  return top.m;
};

/**
 * Validate that all products with demos have proper configurations
 */
export const validateDemoConfigurations = (): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const productsWithDemos = getProductsWithDemos();
  
  productsWithDemos.forEach(product => {
    if (!product.demoConfig) {
      errors.push(`Product ${product.id} missing demoConfig`);
      return;
    }
    
    const config = product.demoConfig;
    if (!config.url || !config.title || !config.buttonText) {
      errors.push(`Product ${product.id} has incomplete demo configuration`);
    }
    
    if (!config.url.startsWith('/')) {
      errors.push(`Product ${product.id} demo URL should start with '/'`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get search term to demo URL mapping for intelligent routing
 */
export const getSearchTermToDemoMapping = (): Record<string, string> => {
  const mappings = getDemoProductMappings();
  const searchMap: Record<string, string> = {};
  
  mappings.forEach(mapping => {
    // Map category and business type terms to demo URLs
    [
      mapping.product.category.toLowerCase(),
      ...(mapping.product.businessTypes || []).map(bt => bt.replace('#', '').toLowerCase()),
      'marketing', 'sales', 'payroll', 'hr', 'facilities', 'management'
    ].forEach(term => {
      if (term.length > 2) {
        searchMap[term] = mapping.demoUrl;
      }
    });
  });
  
  return searchMap;
};

// Development helper: Log demo registry status
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  const validation = validateDemoConfigurations();
  if (!validation.isValid) {
    console.warn('Demo Configuration Issues:', validation.errors);
  } else {
    console.log('✅ All demo configurations are valid');
  }
  
  console.log('📊 Demo Registry Status:', {
    totalProducts: allProducts.length,
    productsWithDemos: getProductsWithDemos().length,
    demoRoutes: getAllDemoRoutes(),
    searchMappings: Object.keys(getSearchTermToDemoMapping()).length
  });
}